import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, TextField, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { useTranslation } from 'react-i18next';

export const hasText = (text) => !!(text || '').trim();

/**
 * A free text kept on a customer or vehicle (internal note, service info), edited in place with
 * explicit Save/Cancel.
 *
 * - `renderView({ action, sx })` draws the saved text, with the edit button as `action`. It is only
 *   called when the text is not empty.
 * - `labels`: translated `label`, `help`, `add`, `edit` and `saveError`.
 * - `testId(part)` names the test ids of the parts: editor, input, save, edit, add.
 * - `onSave(text)` persists the trimmed text and throws on failure.
 * - `maxLength` (optional) caps the field to what the API accepts.
 */
export default function EditableNote({ value, canEdit, onSave, renderView, labels, testId, addIcon, maxLength, sx }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  // A different customer/vehicle (or a fresh load) closes a half-done edit.
  useEffect(() => {
    setEditing(false);
    setError(false);
  }, [value]);

  const startEdit = () => {
    setDraft(value || '');
    setError(false);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(false);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch (err) {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      // pt: room for the outlined field's floating label, which a dialog's scroll area would clip
      <Box sx={{ pt: 1, ...sx }} data-testid={testId('editor')}>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {labels.saveError}
          </Alert>
        )}
        <TextField
          label={labels.label}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          helperText={labels.help}
          multiline
          minRows={2}
          maxRows={8}
          fullWidth
          autoFocus
          inputProps={{ 'data-testid': testId('input'), maxLength }}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
          <Button size="small" onClick={() => setEditing(false)} disabled={saving} sx={{ color: 'text.secondary' }}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
            data-testid={testId('save')}
          >
            {t('Save')}
          </Button>
        </Box>
      </Box>
    );
  }

  if (hasText(value)) {
    return renderView({
      sx,
      action: canEdit ? (
        <Tooltip title={labels.edit}>
          <IconButton size="small" color="inherit" onClick={startEdit} aria-label={labels.edit} data-testid={testId('edit')}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null,
    });
  }

  if (!canEdit) return null;
  return (
    <Box sx={sx}>
      <Button
        size="small"
        startIcon={addIcon || <NoteAddOutlinedIcon />}
        onClick={startEdit}
        sx={{ color: 'text.secondary' }}
        data-testid={testId('add')}
      >
        {labels.add}
      </Button>
    </Box>
  );
}
